import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, ChevronRight, Home, BarChart3, Compass, User, Swords, Users } from "lucide-react";
import banner from "@/assets/centurion-banner-cream.jpg";
import { cn } from "@/lib/utils";

/** Centurion banner: helmet left, temple right, motto + serif title + gold tagline. */
export function CenturionBanner({ title, tagline, motto = "Discipline builds freedom", back }: { title: string; tagline: string; motto?: string; back?: string }) {
  return (
    <header className="relative overflow-hidden rounded-b-2xl">
      <img src={banner} alt="" width={1536} height={640} className="absolute inset-0 h-full w-full object-cover object-left" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-background" />
      {back && (
        <Link to={back} aria-label="Back" className="absolute left-2 top-2 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-background/70 text-foreground/80 hover:text-foreground">
          <ArrowLeft className="h-4 w-4" />
        </Link>
      )}
      <div className="relative flex min-h-[230px] sm:min-h-[280px] flex-col items-center justify-start pl-[44%] pr-3 pt-8 pb-14 text-center">
        <p className="font-serif text-[8px] sm:text-[10px] uppercase tracking-[0.18em] sm:tracking-[0.3em] text-foreground/75">{motto}</p>
        <h1 className="mt-4 font-serif text-[22px] sm:text-4xl font-semibold uppercase leading-[1.1] tracking-[0.02em] sm:tracking-[0.04em] text-foreground">{title}</h1>
        <p className="mt-3 font-serif text-[9px] sm:text-[11px] uppercase tracking-[0.12em] sm:tracking-[0.2em] text-gold-dark">{tagline}</p>
      </div>
    </header>
  );
}

export function CenturionPage({ title, tagline, motto, back = "/dashboard", children, wide }: { title: string; tagline: string; motto?: string; back?: string; children: React.ReactNode; wide?: boolean }) {
  return (
    <main className="min-h-screen overflow-x-hidden bg-background text-foreground">
      <div className={cn("mx-auto", wide ? "max-w-6xl" : "max-w-5xl")}>
        <CenturionBanner title={title} tagline={tagline} motto={motto} back={back} />
        <div className="relative -mt-10 space-y-3 px-4 pb-28">{children}</div>
      </div>
      <CenturionBottomNav />
    </main>
  );
}

const NAV = [
  { to: "/dashboard", label: "Home", icon: Home },
  { to: "/simulation", label: "Simulation", icon: Swords },
  { to: "/community", label: "Community", icon: Users },
  { to: "/profile", label: "Profile", icon: User },
];

/** Mockup bottom row: Home, Progress, Journey, Profile. */
export function CenturionBottomNav({ dark = false }: { dark?: boolean } = {}) {
  const { pathname } = useLocation();
  return (
    <nav aria-label="Member navigation" className={cn("fixed inset-x-0 bottom-0 z-40 border-t pb-[env(safe-area-inset-bottom)]", dark ? "border-gold/60 marble-band shadow-[0_-2px_8px_hsl(var(--foreground)/0.08)]" : "border-border bg-card/95 backdrop-blur")}>
      <div className={cn("mx-auto flex items-stretch justify-around", dark ? "max-w-none" : "max-w-md")}>
        {NAV.map(({ to, label, icon: Icon }) => {
          const active = pathname === to;
          return (
            <Link key={to} to={to} aria-current={active ? "page" : undefined} className={cn("flex min-h-14 min-w-0 flex-1 flex-col items-center justify-center gap-1 py-2.5 font-serif text-[10px] uppercase tracking-normal sm:text-[11px] sm:tracking-[0.12em]", dark ? (active ? "text-gold-dark" : "text-foreground/60") : active ? "text-gold-dark" : "text-foreground/60", dark && "sm:text-sm sm:py-3")}>
              <Icon className="h-5 w-5" />
              {label}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}

export function CenturionCard({ icon, title, date, children, className }: { icon?: React.ReactNode; title?: string; date?: string; children: React.ReactNode; className?: string }) {
  return (
    <section className={cn("rounded-2xl border border-border bg-card p-4 shadow-sm", className)}>
      {(title || date) && (
        <div className="mb-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-foreground">
            {icon && <span className="text-gold">{icon}</span>}
            {title && <h2 className="font-serif text-lg font-semibold">{title}</h2>}
          </div>
          {date && <span className="text-xs text-muted-foreground">{date}</span>}
        </div>
      )}
      {children}
    </section>
  );
}

export function LaurelButton({ children, className, ...rest }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...rest}
      className={cn(
        "flex h-14 w-full items-center justify-center gap-3 rounded-xl font-serif text-base uppercase tracking-[0.12em] text-primary-foreground shadow-gold disabled:opacity-60",
        className,
      )}
      style={{ background: "var(--gradient-gold)" }}
    >
      <Laurel />{children}
    </button>
  );
}

export const todayLabel = () => new Date().toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

/** Compact banner for pop-ups opened from the Kingdom Hub. */
export function CenturionDialogBanner({ title, tagline }: { title: string; tagline: string }) {
  return (
    <div className="relative -mx-6 -mt-6 mb-2 overflow-hidden rounded-t-lg">
      <img src={banner} alt="" width={1536} height={640} className="absolute inset-0 h-full w-full object-cover object-left" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent to-background" />
      <div className="relative flex min-h-[120px] flex-col items-center justify-center pl-[30%] pr-8 py-4 text-center">
        <span className="mb-1.5 h-px w-8 bg-gold" />
        <p className="font-serif text-xl font-semibold text-foreground">{title}</p>
        <p className="mt-0.5 text-[9px] uppercase tracking-[0.3em] text-gold">{tagline}</p>
      </div>
    </div>
  );
}

/** Mockup row card: round icon badge, serif title + date, big serif value, chevron. */
export function ChamberRow({ icon, title, date, value, onClick, children, open }: { icon: React.ReactNode; title: string; date?: string; value?: React.ReactNode; onClick?: () => void; children?: React.ReactNode; open?: boolean }) {
  return (
    <section className="rounded-2xl border border-border/60 bg-card shadow-[0_4px_14px_-6px_hsl(var(--foreground)/0.15)]">
      <button type="button" onClick={onClick} className="flex w-full items-center gap-4 p-4 text-left">
        <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-[radial-gradient(circle_at_35%_30%,hsl(var(--card)),hsl(var(--muted)))] text-gold-dark ring-1 ring-gold/20 [&_svg]:h-9 [&_svg]:w-9 [&_svg]:fill-gold/40 [&_svg]:stroke-[1.75]">{icon}</span>
        <span className="min-w-0 flex-1">
          <span className="block font-serif text-lg leading-tight text-foreground">{title}</span>
          {date && <span className="block font-serif text-sm text-muted-foreground">{date}</span>}
          {value != null && <span className="mt-1 block font-serif text-3xl leading-none text-foreground">{value}</span>}
        </span>
        <ChevronRight className={cn("h-5 w-5 shrink-0 text-foreground/60 transition-transform", open && "rotate-90")} />
      </button>
      {open && children && <div className="border-t border-border/60 p-4">{children}</div>}
    </section>
  );
}

function Laurel() {
  return (
    <svg viewBox="0 0 32 32" className="h-7 w-7" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden>
      <path d="M10 4c-5 5-5 15 2 22M22 4c5 5 5 15-2 22" />
      <path d="M8 9c2 0 3 1 3 3M7 14c2 0 3 1 3 3M8 19c2 0 3 1 3 3M24 9c-2 0-3 1-3 3M25 14c-2 0-3 1-3 3M24 19c-2 0-3 1-3 3" />
    </svg>
  );
}
